import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Lightbulb, Plus, Star, CheckSquare, Trash2, Clock, Loader2, Search, Tag, Palette, Mic, MicOff, X, Link2, Image as ImageIcon, Edit2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_COLORS = [
  { id: 'default', label: 'Default', bg: 'bg-[hsl(var(--card))]', border: 'border-[hsl(var(--border))]' },
  { id: 'red', label: 'Red', bg: 'bg-red-500/8', border: 'border-red-500/25' },
  { id: 'blue', label: 'Blue', bg: 'bg-blue-500/8', border: 'border-blue-500/25' },
  { id: 'green', label: 'Green', bg: 'bg-emerald-500/8', border: 'border-emerald-500/25' },
  { id: 'purple', label: 'Purple', bg: 'bg-purple-500/8', border: 'border-purple-500/25' },
  { id: 'amber', label: 'Amber', bg: 'bg-amber-500/8', border: 'border-amber-500/25' },
];

const TAG_OPTIONS = ['Marketing', 'Sales', 'Product', 'Content', 'Operations', 'Strategy', 'Design', 'Tech', 'Finance', 'General'];
const TAG_COLORS = { 'Marketing': '#3b82f6', 'Sales': '#10b981', 'Product': '#8b5cf6', 'Content': '#f59e0b', 'Operations': '#ef4444', 'Strategy': '#06b6d4', 'Design': '#ec4899', 'Tech': '#6366f1', 'Finance': '#14b8a6', 'General': '#6b7280' };

export default function IdeaParkingPage() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputText, setInputText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('');
  const [showColorPicker, setShowColorPicker] = useState(null);
  const [showTagPicker, setShowTagPicker] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingText, setRecordingText] = useState('');
  const [linkInput, setLinkInput] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingLinkId, setEditingLinkId] = useState(null);
  const [editingLinkValue, setEditingLinkValue] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const recognitionRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchNotes = async () => {
    try { const r = await axios.get(`${API}/notes`, { headers }); setNotes(r.data.notes || []); } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchNotes(); }, []);

  const addNote = async (text, tags = [], color = 'default') => {
    const t = text || inputText;
    if (!t.trim()) return;
    try {
      await axios.post(`${API}/notes`, { text: t.trim(), tags, color, link: linkInput.trim() }, { headers });
      setInputText('');
      setLinkInput('');
      setShowAddForm(false);
      fetchNotes();
      toast.success('Idea saved');
    } catch { toast.error('Failed'); }
  };

  const deleteNote = async (id) => { try { await axios.delete(`${API}/notes/${id}`, { headers }); fetchNotes(); } catch {} };
  const togglePin = async (note) => { try { await axios.put(`${API}/notes/${note.id}`, { pinned: !note.pinned }, { headers }); fetchNotes(); } catch {} };
  const updateNote = async (id, text) => { if (!text.trim()) return; try { await axios.put(`${API}/notes/${id}`, { text: text.trim() }, { headers }); setEditingId(null); fetchNotes(); } catch {} };
  const updateColor = async (id, color) => { try { await axios.put(`${API}/notes/${id}`, { color }, { headers }); setShowColorPicker(null); fetchNotes(); } catch {} };
  const toggleTag = async (note, tag) => {
    const tags = note.tags || [];
    const updated = tags.includes(tag) ? tags.filter(t => t !== tag) : [...tags, tag];
    try { await axios.put(`${API}/notes/${note.id}`, { tags: updated }, { headers }); setShowTagPicker(null); fetchNotes(); } catch {}
  };
  const convertToTask = async (id) => { try { const r = await axios.post(`${API}/notes/${id}/convert-task`, {}, { headers }); if (r.data.success) { toast.success('Converted to task'); fetchNotes(); } } catch { toast.error('Failed'); } };

  const updateLink = async (note, link) => {
    try { await axios.put(`${API}/notes/${note.id}`, { link }, { headers }); fetchNotes(); } catch {}
  };

  const uploadNoteImage = async (note, file) => {
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const img = new window.Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const maxW = 400;
        const scale = Math.min(maxW / img.width, 1);
        canvas.width = img.width * scale;
        canvas.height = img.height * scale;
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.5);
        try { await axios.put(`${API}/notes/${note.id}`, { image: compressed }, { headers }); fetchNotes(); toast.success('Image added'); } catch { toast.error('Upload failed'); }
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Voice recording
  const startRecording = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Browser does not support voice input');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'ms-MY';

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setRecordingText(transcript);
    };

    recognition.onerror = () => { setIsRecording(false); toast.error('Voice input error'); };
    recognition.onend = () => { setIsRecording(false); };

    recognitionRef.current = recognition;
    recognition.start();
    setIsRecording(true);
    setRecordingText('');
  };

  const stopRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    if (recordingText.trim()) {
      addNote(recordingText.trim());
      setRecordingText('');
    }
  };

  const getCardStyle = (color) => {
    const c = CARD_COLORS.find(cc => cc.id === color) || CARD_COLORS[0];
    return `${c.bg} ${c.border}`;
  };

  // All tags used across notes
  const usedTags = [...new Set(notes.flatMap(n => n.tags || []))];

  const filtered = notes.filter(n => {
    if (filter === 'pinned' && !n.pinned) return false;
    if (tagFilter && !(n.tags || []).includes(tagFilter)) return false;
    if (searchQuery && !n.text.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });
  const sortedNotes = [...filtered.filter(n => n.pinned), ...filtered.filter(n => !n.pinned)];

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        <SectionHeader icon={Lightbulb} title="Idea Parking" subtitle="Capture ideas, thoughts & notes on the fly"
          iconBg="bg-gradient-to-br from-[#E63946] to-[#F97316] shadow-[#E63946]/20"
          action={<span className="text-xs text-[hsl(var(--text-muted))] bg-[hsl(var(--surface))] px-2.5 py-1 rounded-lg border border-[hsl(var(--border))]">{notes.length} notes</span>}
        />

        <div className="mt-5 space-y-4 max-w-4xl">
          {/* Input */}
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input value={inputText} onChange={e => setInputText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); addNote(); } }}
                placeholder="What's on your mind? Type and press Enter..."
                className="h-10 text-sm bg-[hsl(var(--card))] border-[hsl(var(--border))] focus:border-[hsl(var(--primary))]/50"
                data-testid="idea-input" />
              <Button variant="outline" className="h-10 px-3" onClick={() => setShowAddForm(!showAddForm)} title="More options">
                <Link2 size={14} />
              </Button>
              <Button className="h-10 px-5 bg-gradient-to-r from-[#E63946] to-[#F97316] text-white font-semibold" onClick={() => addNote()} disabled={!inputText.trim()}>
                <Plus size={16} className="mr-1.5" /> Add
              </Button>
              <Button variant="outline" className={`h-10 px-3 ${isRecording ? 'border-red-500 text-red-400 bg-red-500/10 animate-pulse' : ''}`}
                onClick={isRecording ? stopRecording : startRecording} title="Voice input">
                {isRecording ? <MicOff size={16} /> : <Mic size={16} />}
              </Button>
            </div>
            {showAddForm && (
              <Input value={linkInput} onChange={e => setLinkInput(e.target.value)} placeholder="Add reference link (optional)..."
                className="h-9 text-xs bg-[hsl(var(--card))] border-[hsl(var(--border))]" />
            )}
            {isRecording && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/5 border border-red-500/20">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                <span className="text-xs text-red-400">Listening...</span>
                <span className="text-xs text-white/70 flex-1">{recordingText || 'Start speaking...'}</span>
                <Button size="sm" className="h-6 text-[10px] bg-red-500 text-white" onClick={stopRecording}>Stop & Save</Button>
              </div>
            )}
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-[250px]">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--text-muted))]" />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search notes..." className="pl-9 h-8 text-xs bg-[hsl(var(--card))]" />
            </div>
            <div className="flex gap-1">
              {['all', 'pinned'].map(f => (
                <button key={f} onClick={() => setFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold transition-all ${filter === f ? 'bg-[hsl(var(--primary))] text-white' : 'bg-[hsl(var(--card))] text-[hsl(var(--text-muted))] border border-[hsl(var(--border))] hover:text-white'}`}>
                  {f === 'all' ? 'All' : 'Pinned'}
                </button>
              ))}
            </div>
            {usedTags.length > 0 && (
              <div className="flex gap-1 flex-wrap">
                {tagFilter && <button onClick={() => setTagFilter('')} className="px-2 py-1 rounded text-[9px] bg-[hsl(var(--primary))]/20 text-[hsl(var(--primary))] border border-[hsl(var(--primary))]/30">Clear</button>}
                {usedTags.map(tag => (
                  <button key={tag} onClick={() => setTagFilter(tagFilter === tag ? '' : tag)}
                    className={`px-2 py-1 rounded text-[9px] font-medium border transition-all ${tagFilter === tag ? 'text-white' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                    style={tagFilter === tag ? { backgroundColor: `${TAG_COLORS[tag] || '#6b7280'}30`, borderColor: `${TAG_COLORS[tag] || '#6b7280'}50`, color: TAG_COLORS[tag] } : { borderColor: 'hsl(var(--border))' }}>
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Notes Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 size={20} className="animate-spin text-[hsl(var(--primary))]" /></div>
          ) : sortedNotes.length === 0 ? (
            <div className="py-16 text-center">
              <Lightbulb size={40} className="mx-auto mb-3 text-[hsl(var(--text-muted))]/15" />
              <p className="text-sm text-[hsl(var(--text-muted))]">{searchQuery || tagFilter ? 'No matching notes' : 'No ideas yet'}</p>
              <p className="text-xs text-[hsl(var(--text-muted))]/60 mt-1">Start typing above to capture your first idea</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {sortedNotes.map(note => (
                <div key={note.id}
                  className={`group relative rounded-xl border p-4 transition-all hover:shadow-lg ${note.pinned ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5' : getCardStyle(note.color)}`}
                  data-testid={`note-${note.id}`}>
                  {note.pinned && <div className="absolute top-0 left-0 right-0 h-[2px] rounded-t-xl bg-gradient-to-r from-[#E63946] to-[#F97316]" />}

                  {editingId === note.id ? (
                    <textarea autoFocus defaultValue={note.text}
                      onBlur={e => updateNote(note.id, e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); updateNote(note.id, e.target.value); } if (e.key === 'Escape') setEditingId(null); }}
                      className="w-full min-h-[60px] text-[13px] bg-transparent border border-[hsl(var(--primary))]/40 rounded-lg p-2 text-[hsl(var(--text-primary))] outline-none resize-none" />
                  ) : (
                    <p className="text-[13px] text-[hsl(var(--text-primary))]/90 leading-relaxed cursor-pointer whitespace-pre-wrap" onClick={() => setEditingId(note.id)}>
                      {note.text}
                    </p>
                  )}

                  {/* Tags */}
                  {(note.tags || []).length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {note.tags.map(tag => (
                        <span key={tag} className="text-[8px] px-1.5 py-0.5 rounded font-semibold" style={{ backgroundColor: `${TAG_COLORS[tag] || '#6b7280'}20`, color: TAG_COLORS[tag] || '#6b7280' }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Link — editable inline */}
                  {editingLinkId === note.id ? (
                    <div className="flex items-center gap-1 mt-2">
                      <Link2 size={10} className="text-[#3b82f6] shrink-0" />
                      <input autoFocus value={editingLinkValue} onChange={e => setEditingLinkValue(e.target.value)}
                        onBlur={() => { updateLink(note, editingLinkValue); setEditingLinkId(null); }}
                        onKeyDown={e => { if (e.key === 'Enter') { updateLink(note, editingLinkValue); setEditingLinkId(null); } if (e.key === 'Escape') setEditingLinkId(null); }}
                        placeholder="https://..."
                        className="flex-1 h-6 px-2 text-[10px] bg-transparent border border-[#3b82f6]/40 rounded text-[#3b82f6] outline-none" />
                    </div>
                  ) : note.link ? (
                    <div className="flex items-center gap-1 mt-2 group/link">
                      <a href={note.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-[10px] text-[#3b82f6] hover:underline truncate flex-1">
                        <Link2 size={10} className="shrink-0" /> {note.link}
                      </a>
                      <button onClick={() => { setEditingLinkId(note.id); setEditingLinkValue(note.link); }} className="opacity-0 group-hover/link:opacity-100 p-0.5 text-[hsl(var(--text-muted))] hover:text-white">
                        <Edit2 size={8} />
                      </button>
                    </div>
                  ) : null}

                  {/* Image — click to lightbox */}
                  {note.image && (
                    <img src={note.image} alt="" onClick={() => setLightboxImage(note.image)}
                      className="mt-2 rounded-lg h-[80px] object-cover w-full cursor-pointer hover:opacity-80 transition-opacity" />
                  )}

                  {/* Footer */}
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-[hsl(var(--border))]/30">
                    <span className="text-[9px] text-[hsl(var(--text-muted))]">
                      <Clock size={9} className="inline mr-1" />
                      {new Date(note.created_at).toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                    <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => togglePin(note)} className={`p-1.5 rounded-md transition-all ${note.pinned ? 'text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15' : 'text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))]'}`} title="Pin">
                        <Star size={12} className={note.pinned ? 'fill-current' : ''} />
                      </button>
                      <div className="relative">
                        <button onClick={() => setShowTagPicker(showTagPicker === note.id ? null : note.id)} className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-all" title="Tags">
                          <Tag size={12} />
                        </button>
                        {showTagPicker === note.id && (
                          <div className="absolute bottom-8 right-0 z-50 w-36 p-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-xl space-y-0.5">
                            {TAG_OPTIONS.map(tag => (
                              <button key={tag} onClick={() => toggleTag(note, tag)}
                                className={`w-full text-left px-2 py-1 rounded text-[10px] transition-all ${(note.tags || []).includes(tag) ? 'font-bold' : 'text-[hsl(var(--text-muted))] hover:text-white'}`}
                                style={(note.tags || []).includes(tag) ? { color: TAG_COLORS[tag], backgroundColor: `${TAG_COLORS[tag]}15` } : {}}>
                                {(note.tags || []).includes(tag) ? '✓ ' : ''}{tag}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="relative">
                        <button onClick={() => setShowColorPicker(showColorPicker === note.id ? null : note.id)} className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-all" title="Color">
                          <Palette size={12} />
                        </button>
                        {showColorPicker === note.id && (
                          <div className="absolute bottom-8 right-0 z-50 p-2 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))] shadow-xl flex gap-1.5">
                            {CARD_COLORS.map(c => (
                              <button key={c.id} onClick={() => updateColor(note.id, c.id)}
                                className={`w-6 h-6 rounded-md border-2 transition-all ${c.bg} ${note.color === c.id ? 'border-white scale-110' : 'border-transparent hover:scale-110'}`}
                                title={c.label} />
                            ))}
                          </div>
                        )}
                      </div>
                      <button onClick={() => { setEditingLinkId(note.id); setEditingLinkValue(note.link || ''); }} className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[#3b82f6] hover:bg-[#3b82f6]/10 transition-all" title={note.link ? 'Edit link' : 'Add link'}>
                        <Link2 size={12} />
                      </button>
                      <label className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[#f59e0b] hover:bg-[#f59e0b]/10 transition-all cursor-pointer" title="Add image">
                        <ImageIcon size={12} />
                        <input type="file" accept="image/*" className="hidden" onChange={e => { if (e.target.files?.[0]) uploadNoteImage(note, e.target.files[0]); e.target.value = ''; }} />
                      </label>
                      <button onClick={() => convertToTask(note.id)} className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-[#10b981] hover:bg-[#10b981]/10 transition-all" title="Convert to Task">
                        <CheckSquare size={12} />
                      </button>
                      <button onClick={() => deleteNote(note.id)} className="p-1.5 rounded-md text-[hsl(var(--text-muted))] hover:text-red-400 hover:bg-red-500/10 transition-all" title="Delete">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Image Lightbox */}
        {lightboxImage && (
          <div className="fixed inset-0 z-[9999] bg-black/80 flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
            <div className="relative max-w-3xl max-h-[85vh]" onClick={e => e.stopPropagation()}>
              <button onClick={() => setLightboxImage(null)} className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-white/10 backdrop-blur flex items-center justify-center text-white hover:bg-white/20 z-10">
                <X size={16} />
              </button>
              <img src={lightboxImage} alt="" className="max-w-full max-h-[85vh] rounded-xl object-contain" />
            </div>
          </div>
        )}
      </div>
    </ResponsiveLayout>
  );
}
